import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  claimAnnouncement,
  SERVICES_ANNOUNCEMENT_ID,
  type AnnouncementClaim,
} from './api';
import { ServicesAnnouncementDialog } from './ServicesAnnouncementPreview';

interface ServicesAnnouncementGateProps {
  userId: string;
  prefersDark: boolean;
}

const announcementClaimKey = (userId: string) =>
  ['announcement-claim', userId, SERVICES_ANNOUNCEMENT_ID] as const;

/**
 * Claims and, only for the winning request, displays the services launch ad.
 * TanStack Query shares the in-flight POST across React StrictMode remounts;
 * the backend's unique constraint handles separate tabs and devices.
 */
export function ServicesAnnouncementGate({
  userId,
  prefersDark,
}: ServicesAnnouncementGateProps) {
  const queryClient = useQueryClient();
  const queryKey = announcementClaimKey(userId);
  const { data } = useQuery({
    queryKey,
    queryFn: () => claimAnnouncement(SERVICES_ANNOUNCEMENT_ID),
    staleTime: Infinity,
    retry: 2,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const handleOpenChange = (open: boolean) => {
    if (open) return;

    // Prevent this cached first-claim response from reopening the dialog if
    // the protected route remounts before the browser is refreshed.
    queryClient.setQueryData<AnnouncementClaim>(queryKey, (current) =>
      current ? { ...current, shouldDisplay: false } : current,
    );
  };

  if (!data?.shouldDisplay) return null;

  return (
    <ServicesAnnouncementDialog
      open
      onOpenChange={handleOpenChange}
      theme={prefersDark ? 'dark' : 'light'}
    />
  );
}
